import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Users, MagnifyingGlass, ShieldCheck, X, Check, 
  Trash, UserCircle, FunnelSimple, CaretDown
} from '@phosphor-icons/react';

const ROLE_CONFIG = {
  visitor: { label: 'Visitor', color: 'gray', description: 'View-only access' },
  contributor: { label: 'Contributor', color: 'blue', description: 'Can create drafts' },
  writer: { label: 'Writer', color: 'green', description: 'Can publish own content' },
  trusted_writer: { label: 'Trusted Writer', color: 'yellow', description: 'Auto-publish enabled' },
  editor: { label: 'Editor', color: 'purple', description: 'Manage all content' },
  admin: { label: 'Admin', color: 'red', description: 'Full system access' }
};

export default function CmsUserManagement() {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersRes, statsRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/stats`, { withCredentials: true })
      ]);
      
      console.log('Users API Response:', usersRes.data);
      console.log('Users array:', Array.isArray(usersRes.data) ? `${usersRes.data.length} users` : 'Not an array');
      
      setUsers(usersRes.data || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}/role`,
        { role: newRole },
        { withCredentials: true }
      );
      
      toast.success('User role updated');
      setShowRoleModal(false);
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         user.display_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = !roleFilter || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <Users size={32} weight="fill" className="text-primary" />
          User & Role Management
        </h1>
        <p className="text-muted-foreground mt-1">
          Manage user accounts and assign roles
        </p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="text-sm text-muted-foreground">Total Users</div>
            <div className="text-2xl font-bold">{stats.total_users}</div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="text-sm text-muted-foreground">Writers</div>
            <div className="text-2xl font-bold">{stats.total_writers}</div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="text-sm text-muted-foreground">Editors</div>
            <div className="text-2xl font-bold">{stats.total_editors}</div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="text-sm text-muted-foreground">Admins</div>
            <div className="text-2xl font-bold">{stats.total_admins}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by email or name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg"
            />
          </div>

          <div className="flex items-center gap-2">
            <FunnelSimple size={20} className="text-muted-foreground" />
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="px-4 py-2 bg-background border border-border rounded-lg"
            >
              <option value="">All Roles</option>
              {Object.entries(ROLE_CONFIG).map(([role, config]) => (
                <option key={role} value={role}>{config.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {/* Debug info */}
        {process.env.NODE_ENV === 'development' && (
          <div className="px-4 py-2 bg-yellow-50 dark:bg-yellow-900/20 text-xs">
            Debug: {users.length} total users, {filteredUsers.length} after filters
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold">User</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Email</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Role</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Joined</th>
                <th className="px-6 py-3 text-center text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredUsers.map(user => {
                const roleConfig = ROLE_CONFIG[user.role] || ROLE_CONFIG.visitor;
                return (
                  <tr key={user.user_id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <UserCircle size={32} weight="fill" className="text-muted-foreground" />
                        <div>
                          <div className="font-medium">{user.display_name || 'Unnamed User'}</div>
                          <div className="text-xs text-muted-foreground">{user.user_id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">{user.email}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-${roleConfig.color}-500/10 text-${roleConfig.color}-600 dark:text-${roleConfig.color}-400`}>
                        <ShieldCheck size={14} weight="fill" />
                        {roleConfig.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            setShowRoleModal(true);
                          }}
                          className="px-3 py-1 text-sm bg-primary/10 text-primary rounded hover:bg-primary/20 transition-colors"
                        >
                          Change Role
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredUsers.length === 0 && (
          <div className="py-12 text-center text-muted-foreground">
            No users found matching your filters
          </div>
        )}
      </div>

      {/* Role Change Modal */}
      {showRoleModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowRoleModal(false)}>
          <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Change User Role</h3>
              <button onClick={() => setShowRoleModal(false)} className="text-muted-foreground hover:text-foreground">
                <X size={20} />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-muted-foreground mb-2">
                Changing role for: <span className="font-medium text-foreground">{selectedUser.email}</span>
              </p>
              <p className="text-sm text-muted-foreground">
                Current role: <span className="font-medium text-foreground">{ROLE_CONFIG[selectedUser.role]?.label}</span>
              </p>
            </div>

            <div className="space-y-2">
              {Object.entries(ROLE_CONFIG).map(([role, config]) => (
                <button
                  key={role}
                  onClick={() => handleUpdateRole(selectedUser.user_id, role)}
                  disabled={selectedUser.role === role}
                  className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${
                    selectedUser.role === role
                      ? 'bg-muted/50 border-border cursor-not-allowed'
                      : 'hover:bg-muted border-border hover:border-primary'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{config.label}</div>
                      <div className="text-xs text-muted-foreground">{config.description}</div>
                    </div>
                    {selectedUser.role === role && (
                      <Check size={20} className="text-primary" weight="bold" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
